import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePets } from '../context/PetContext';
import { sendChatMessage } from '../api/client';
import { PetVisual } from '../components/visuals';
import { ArrowIcon, ChatIcon } from '../components/icons';
import './Chat.css';

// Local storage helpers
const STORAGE_KEY = 'petlings_chat_history';

function loadLocalHistory(slotIndex) {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return all[slotIndex] || [];
  } catch { return []; }
}

function saveLocalHistory(slotIndex, messages) {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    all[slotIndex] = messages;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch { /* storage full or unavailable */ }
}

function clearLocalHistory(slotIndex) {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    delete all[slotIndex];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {}
}

function formatTime(ts) {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatDateSeparator(ts) {
  const d = new Date(ts);
  const now = new Date();
  const today = now.toDateString();
  const yesterday = new Date(now - 86400000).toDateString();
  if (d.toDateString() === today) return 'Today';
  if (d.toDateString() === yesterday) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

function shouldShowDateSeparator(messages, index) {
  if (index === 0) return true;
  const curr = new Date(messages[index].timestamp).toDateString();
  const prev = new Date(messages[index - 1].timestamp).toDateString();
  return curr !== prev;
}

export default function Chat() {
  const { slot } = useParams();
  const slotNum = parseInt(slot, 10);
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { pets } = usePets();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const pet = pets.find((p) => p.slot_index === slotNum);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  // Load chat history from local storage
  useEffect(() => {
    if (!pet) return;
    const history = loadLocalHistory(slotNum);
    if (history.length > 0) {
      setMessages(history);
    } else {
      // First-time greeting
      const greeting = {
        role: 'pet',
        content: `Hi there! I'm ${pet.pet_name}. Ask me anything about Minecraft, or just say hi!`,
        timestamp: new Date().toISOString(),
        petName: pet.pet_name,
      };
      setMessages([greeting]);
      saveLocalHistory(slotNum, [greeting]);
    }
  }, [pet, slotNum]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Save to local storage whenever messages change
  const persistMessages = useCallback((msgs) => {
    setMessages(msgs);
    saveLocalHistory(slotNum, msgs);
  }, [slotNum]);

  const handleClear = () => {
    clearLocalHistory(slotNum);
    const greeting = {
      role: 'pet',
      content: `*stretches* Fresh start! What shall we talk about, friend?`,
      timestamp: new Date().toISOString(),
      petName: pet.pet_name,
    };
    persistMessages([greeting]);
    setShowClearConfirm(false);
  };

  if (authLoading) return <div className="petview-loading">Loading...</div>;
  if (!pet) {
    return (
      <div className="petview-empty">
        <p>No pet in this slot.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/pets')}>Back to Kennel</button>
      </div>
    );
  }

  // Avatars are faces, so always the 2D art whatever the site's switch says.
  const PetAvatar = ({ size }) => <PetVisual petId={pet.pet_type_id} size={size} forceMode="2d" />;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMsg = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString(),
    };
    const updatedWithUser = [...messages, userMsg];
    persistMessages(updatedWithUser);
    setInput('');
    setSending(true);

    try {
      const res = await sendChatMessage(input.trim(), pet.pet_name, pet.pet_type_id, slotNum, pet.mood);
      const petReply = {
        role: 'pet',
        content: res.data.response || res.data.message || '...',
        timestamp: new Date().toISOString(),
        petName: pet.pet_name,
      };
      persistMessages([...updatedWithUser, petReply]);
    } catch {
      const fallbackReplies = [
        `*${pet.pet_name} looks at you curiously*`,
        `*${pet.pet_name} wags happily*`,
        `*${pet.pet_name} tilts their head*`,
        `Meow? ...I mean, hi!`,
      ];
      const petReply = {
        role: 'pet',
        content: fallbackReplies[Math.floor(Math.random() * fallbackReplies.length)],
        timestamp: new Date().toISOString(),
        petName: pet.pet_name,
      };
      persistMessages([...updatedWithUser, petReply]);
    }
    setSending(false);
    inputRef.current?.focus();
  };

  return (
    <div className="chat-page">
      <div className="container">
        <button className="back-btn" onClick={() => navigate(`/pets/${slotNum}`)}>
          <ArrowIcon size={18} direction="left" /> Back to {pet.pet_name}
        </button>

        <div className="chat-container card">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-avatar">
              <PetAvatar size={36} />
            </div>
            <div className="chat-header-info">
              <h2>{pet.pet_name}</h2>
              <span className={`kennel-mood mood-${(pet.mood || 'happy').toLowerCase()}`}>
                {(pet.mood || 'happy').toLowerCase()}
              </span>
            </div>
            <div className="chat-header-actions">
              <button
                className="chat-action-link"
                onClick={() => setShowClearConfirm(!showClearConfirm)}
                title="Clear chat history"
              >
                Clear
              </button>
              <button
                className="chat-clear-btn"
                onClick={() => navigate(`/pets/${slotNum}`)}
                title="Close chat"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          {/* Clear confirmation */}
          {showClearConfirm && (
            <div className="chat-clear-confirm">
              <span>Clear all chat history with {pet.pet_name}?</span>
              <div className="chat-clear-actions">
                <button className="btn btn-ghost btn-sm" onClick={() => setShowClearConfirm(false)}>Cancel</button>
                <button className="btn btn-sm chat-clear-yes" onClick={handleClear}>Clear</button>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="chat-messages">
            {/* Local storage notice */}
            <div className="chat-local-notice">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1" />
                <path d="M6 4V7M6 8.5V8.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
              </svg>
              Chat history is saved locally on this device
            </div>

            {messages.map((msg, i) => (
              <React.Fragment key={i}>
                {shouldShowDateSeparator(messages, i) && (
                  <div className="chat-date-separator">
                    <span>{formatDateSeparator(msg.timestamp)}</span>
                  </div>
                )}
                <div className={`chat-bubble chat-${msg.role}`}>
                  {msg.role === 'pet' && (
                    <div className="chat-bubble-avatar">
                      <PetAvatar size={28} />
                    </div>
                  )}
                  <div className="chat-bubble-body">
                    {msg.role === 'pet' && (
                      <span className="chat-bubble-name">{msg.petName || pet.pet_name}</span>
                    )}
                    <div className="chat-bubble-content">
                      <p>{msg.content}</p>
                    </div>
                    <span className="chat-bubble-time">{formatTime(msg.timestamp)}</span>
                  </div>
                </div>
              </React.Fragment>
            ))}
            {sending && (
              <div className="chat-bubble chat-pet">
                <div className="chat-bubble-avatar">
                  <PetAvatar size={28} />
                </div>
                <div className="chat-bubble-body">
                  <span className="chat-bubble-name">{pet.pet_name}</span>
                  <div className="chat-bubble-content chat-typing">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form className="chat-input-row" onSubmit={handleSend}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Say something to ${pet.pet_name}...`}
              disabled={sending}
              maxLength={500}
              autoFocus
            />
            <button type="submit" className="btn btn-primary chat-send" disabled={!input.trim() || sending}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M2 9L16 2L9 16L8 10L2 9Z" fill="currentColor" />
              </svg>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
